import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, ExternalLink, Instagram, X } from 'lucide-react';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';
// CategoryNav removed
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';

const GROCERY_FIELDS: FieldDef[] = [
  { key: "name", label: "Store name", required: true },
  { key: "address", label: "Address", type: "textarea" },
  { key: "location", label: "Location", placeholder: "Frisco" },
  { key: "type", label: "Type", placeholder: "Fresh Meat" },
  { key: "mapsUrl", label: "Google Maps URL", type: "url", placeholder: "https://…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

// ── Unique generated storefront images (one per brand, 14 brands) ──
const IMG_INDIA_CASH_CARRY = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783457018193-o07bpd.png';
const IMG_PATEL_BROTHERS = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783782657145-34hum.png';
const IMG_PATEL_BROTHERS_IRVING = IMG_PATEL_BROTHERS;
const IMG_INDIA_BAZAAR = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783782467213-o0qrh.png';
const IMG_INDIA_BAZAAR_IRVING = IMG_INDIA_BAZAAR;
const IMG_INDIA_BAZAAR_PLANO_SPRING = IMG_INDIA_BAZAAR;
const IMG_SPICE_RACK = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783788106577-f3gpc.png';
const IMG_DESI_CHOWRASTHA = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/48783dde291c.jpeg';
const IMG_SPICE_BAZAAR = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/7519359837a9.jpeg';
const IMG_INDOPAK = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784611790566-70z4i9.png';
const IMG_DESI_MANDI = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/acfb6c1f7c3c.jpeg';
const IMG_INDIA_METRO = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784611396048-hmomcf.png';
const IMG_SUBZI_MANDI = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/8077720e1dbf.jpeg';
const IMG_TAJ_GROCERY = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/9305b414cac8.jpeg';
const IMG_AL_MARKAZ_2 = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784688168153-prym5.png';
const IMG_DESI_BROTHERS = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-11/2551cf87a183.jpeg';
const IMG_INDO_PAK = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784611867143-xisw0rg.png';
const IMG_FARM2COOK = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/24c659cf592c.png';
const IMG_MEAT_WALA = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/c223742e7f48.png';
const IMG_SPICE_RACK_FRISCO_2 = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784611565297-12hzho.png';

const LOCATION_FILTERS = ['Addison', 'Allen', 'Anna', 'Arlington', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];
const HERO_LOCATIONS = ['All Locations', 'Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Prosper', 'Richardson', 'Rockwall', 'Rowlett', 'Southlake', 'Terrell', 'The Colony', 'Other'];

const GROCERY_TYPES = [
  { name: 'Fresh Meat', icon: '🥩' },
  { name: 'Halal', icon: '🕌' },
];

// "Fresh Meat" filter (Explore by Type): an explicit allowlist rather than a
// per-store `type` field, so it always matches exactly what was requested —
// five specific stores by id, plus every card named "Desi Chowrastha" or
// "FARM2COOK" regardless of which location/branch it is.
const FRESH_MEAT_IDS = new Set([
  'india-cash-carry',
  'desi-mandi',
  'meat-wala-mckinney',
  'al-markaz-grocery',
  'spice-rack-coit',
]);
const FRESH_MEAT_NAMES = new Set(['desi chowrastha', 'farm2cook']);
function isFreshMeatStore(store: { id?: string; name?: string }): boolean {
  const name = String(store.name || '').trim().toLowerCase();
  // Explicit exclusion: no India Bazaar card (any location/branch) should
  // ever show under the "Fresh Meat" filter.
  if (name.includes('india bazaar') || name.includes('india bazar') || name.includes('bazaar')) return false;
  if (store.id && FRESH_MEAT_IDS.has(store.id)) return true;
  return FRESH_MEAT_NAMES.has(name);
}

// Every grocery store's public detail-page URL is derived from its stable id,
// or (for admin-added cards with no preset id) its name — same pattern as
// dessertSlug in Desserts.tsx. Used by both the card link and
// GroceryDetailPage's lookup.
export function grocerySlug(s: { id?: string; name?: string }) {
  return slugify(s.id || s.name || '');
}

export const GROCERY_STORES = [
  {
    id: 'swadeshi-frisco',
    name: 'Swadeshi',
    address: '14300 State Hwy 121 #100, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'Swadeshi+14300+State+Hwy+121+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Swadeshi+14300+State+Hwy+121+Frisco+TX+75035',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147718108-zdlyb5j.jpg',
  },
  {
    id: 'swadeshi-lewisville',
    name: 'Swadeshi',
    address: '112 Castle Hills Dr, Lewisville, TX 75056',
    location: 'Lewisville',
    placeQuery: 'Swadeshi+112+Castle+Hills+Dr+Lewisville+TX+75056',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Swadeshi+112+Castle+Hills+Dr+Lewisville+TX+75056',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147809282-88xje6.jpg',
  },
  {
    id: 'india-cash-carry',
    name: 'India Cash & Carry',
    address: '1900 Market Pl Blvd Suite 110, Irving, TX 75063',
    location: 'Irving',
    placeQuery: 'India+Cash+%26+Carry+1900+Market+Pl+Blvd+Irving+TX+75063',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Cash+%26+Carry+1900+Market+Pl+Blvd+Suite+110+Irving+TX+75063',
    image: IMG_INDIA_CASH_CARRY,
    type: 'Fresh Meat',
  },
  {
    id: 'patel-brothers-irving',
    name: 'Patel Brothers Irving',
    address: '1009 W Rochelle Rd, Irving, TX 75062',
    location: 'Irving',
    placeQuery: 'Patel+Brothers+1009+W+Rochelle+Rd+Irving+TX+75062',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Patel+Brothers+1009+W+Rochelle+Rd+Irving+TX+75062',
    image: IMG_PATEL_BROTHERS_IRVING,
  },
  {
    id: 'india-bazaar-irving-2',
    name: 'India Bazaar Irving',
    address: '8600 N MacArthur Blvd, Irving, TX 75063',
    location: 'Irving',
    placeQuery: 'India+Bazaar+8600+N+MacArthur+Blvd+Irving+TX+75063',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Bazaar+8600+N+MacArthur+Blvd+Irving+TX+75063',
    image: IMG_INDIA_BAZAAR_IRVING,
  },
  {
    id: 'desi-chowrastha-rolater',
    name: 'Desi Chowrastha',
    address: '15851 Rolater Rd, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'Desi+Chowrastha+15851+Rolater+Rd+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Desi+Chowrastha+15851+Rolater+Rd+Frisco+TX+75035',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783784302416-vvtksp.png',
    type: 'Fresh Meat', // dupe-guard: already had type, keep
  },
  {
    id: 'spice-rack-grocery-frisco',
    name: 'Spice Rack Grocery Frisco',
    address: '10010 Coit Rd, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'Spice+Rack+Grocery+Frisco+10010+Coit+Rd+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Spice+Rack+Grocery+Frisco+10010+Coit+Rd+Frisco+TX+75035',
    image: IMG_SPICE_RACK_FRISCO_2,
  },
  {
    id: 'patel-brothers-plano',
    name: 'Patel Brothers Plano',
    address: '1717 N Central Expy, Plano, TX 75075',
    location: 'Plano',
    placeQuery: 'Patel+Brothers+1717+N+Central+Expy+Plano+TX+75075',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Patel+Brothers+1717+N+Central+Expy+Plano+TX+75075',
    image: IMG_PATEL_BROTHERS,
  },
  {
    id: 'spice-bazaar',
    name: 'Spice Bazaar',
    address: '555 W McDermott Dr, Allen, TX 75013',
    location: 'Allen',
    placeQuery: 'Spice+Bazaar+555+W+McDermott+Dr+Allen+TX+75013',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Spice+Bazaar+555+W+McDermott+Dr+Allen+TX+75013',
    image: IMG_SPICE_BAZAAR,
  },
  {
    id: 'taj-grocery',
    name: 'Taj Grocery',
    address: '2752 N MacArthur Blvd, Lewisville, TX 75067',
    location: 'Lewisville',
    placeQuery: 'Taj+Grocery+2752+N+MacArthur+Blvd+Lewisville+TX+75067',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Taj+Grocery+2752+N+MacArthur+Blvd+Lewisville+TX+75067',
    image: IMG_TAJ_GROCERY,
  },
  {
    id: 'subzi-mandi-plano',
    name: 'Subzi Mandi',
    address: '6921 Independence Pkwy #210, Plano, TX 75023',
    location: 'Plano',
    placeQuery: 'Subzi+Mandi+6921+Independence+Pkwy+Plano+TX+75023',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Subzi+Mandi+6921+Independence+Pkwy+Plano+TX+75023',
    image: IMG_SUBZI_MANDI,
  },
  {
    id: 'indopak-spring-creek',
    name: 'Indopak Super Market',
    address: '4285, 2060 Spring Creek Pkwy, Plano, TX 75023',
    location: 'Plano',
    placeQuery: 'Indopak+Super+Market+2060+Spring+Creek+Pkwy+Plano+TX+75023',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Indopak+Super+Market+2060+Spring+Creek+Pkwy+Plano+TX+75023',
    image: IMG_INDOPAK,
  },
  {
    id: 'indopak-richardson',
    name: 'Indopak Super Market',
    address: '323 E Polk St, Richardson, TX 75081',
    location: 'Richardson',
    placeQuery: 'Indopak+Super+Market+323+E+Polk+St+Richardson+TX+75081',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Indopak+Super+Market+323+E+Polk+St+Richardson+TX+75081',
    image: IMG_INDOPAK,
  },
  {
    id: 'al-markaz',
    name: 'Al Markaz',
    address: '1205 W Trinity Mills Rd #112, Carrollton, TX 75006',
    location: 'Carrollton',
    placeQuery: 'Al+Markaz+1205+W+Trinity+Mills+Rd+Carrollton+TX+75006',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Al+Markaz+1205+W+Trinity+Mills+Rd+Carrollton+TX+75006',
    image: IMG_AL_MARKAZ_2,
    type: ['Fresh Meat', 'Halal'],
  },
  {
    id: 'desi-brothers-farmers-market',
    name: 'Desi Brothers Farmers Market',
    address: '100 S Central Expy Suite 26C, Richardson, TX 75080',
    location: 'Richardson',
    placeQuery: 'Desi+Brothers+Farmers+Market+100+S+Central+Expy+Richardson+TX+75080',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Desi+Brothers+Farmers+Market+100+S+Central+Expy+Richardson+TX+75080',
    image: IMG_DESI_BROTHERS,
  },
  {
    id: 'india-metro-mckinney',
    name: 'India Metro Hypermarket',
    address: '1480 Independence Pkwy Ste 220, McKinney, TX 75072',
    location: 'McKinney',
    placeQuery: 'India+Metro+Hypermarket+1480+Independence+Pkwy+McKinney+TX+75072',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Metro+Hypermarket+1480+Independence+Pkwy+McKinney+TX+75072',
    image: IMG_INDIA_METRO,
  },
  {
    id: 'desi-mandi',
    name: 'Desi Mandi',
    address: '420 E Round Grove Rd unit 700, Lewisville, TX 75067',
    location: 'Lewisville',
    placeQuery: 'Desi+Mandi+420+E+Round+Grove+Rd+Lewisville+TX+75067',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Desi+Mandi+420+E+Round+Grove+Rd+Lewisville+TX+75067',
    image: IMG_DESI_MANDI,
    type: ['Fresh Meat', 'Halal'],
  },
  {
    id: 'india-bazaar-lewisville-2',
    name: 'India Bazaar Lewisville',
    address: '2151 Edmonds Ln, Lewisville, TX 75067',
    location: 'Lewisville',
    placeQuery: 'India+Bazaar+2151+Edmonds+Ln+Lewisville+TX+75067',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Bazaar+2151+Edmonds+Ln+Lewisville+TX+75067',
    image: IMG_INDIA_BAZAAR,
  },
  {
    id: 'indopak-lewisville',
    name: 'Indopak Super Market',
    address: '297 W Round Grove Rd, Lewisville, TX 75067',
    location: 'Lewisville',
    placeQuery: 'Indopak+Super+Market+297+W+Round+Grove+Rd+Lewisville+TX+75067',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Indopak+Super+Market+297+W+Round+Grove+Rd+Lewisville+TX+75067',
    image: IMG_INDOPAK,
  },
  {
    id: 'chalo-india-frisco',
    name: 'Chalo India',
    address: '11010 Panther Creek Pkwy #1, Frisco, TX 75034',
    location: 'Frisco',
    placeQuery: 'Chalo+India+11010+Panther+Creek+Pkwy+Frisco+TX+75034',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Chalo+India+11010+Panther+Creek+Pkwy+%231+Frisco+TX+75034',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784691566550-drnie7.png',
    type: ['Fresh Meat', 'Halal'],
  },
  {
    id: 'india-metro-little-elm',
    name: 'India Metro Hypermarket',
    address: '3014 FM 423 Suite 100, Little Elm, TX 75068',
    location: 'Little Elm',
    placeQuery: 'India+Metro+Hypermarket+3014+FM+423+Little+Elm+TX+75068',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Metro+Hypermarket+3014+FM+423+Little+Elm+TX+75068',
    image: IMG_INDIA_METRO,
  },
  {
    id: 'indo-pak-carrollton',
    name: 'Indo-Pak',
    address: '2548 Dickerson Pkwy, Carrollton, TX 75006',
    location: 'Carrollton',
    placeQuery: 'Indo-Pak+2548+Dickerson+Pkwy+Carrollton+TX+75006',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Indo-Pak+2548+Dickerson+Pkwy+Carrollton+TX+75006',
    image: IMG_INDO_PAK,
  },
  {
    id: 'india-bazaar-eldorado',
    name: 'India Bazaar Eldorado',
    address: '14339 Eldorado Pkwy Suite 110, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'India+Bazaar+14339+Eldorado+Pkwy+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Bazaar+Eldorado+14339+Eldorado+Pkwy+Suite+110+Frisco+TX+75035',
    image: IMG_INDIA_BAZAAR,
  },
  {
    id: 'india-bazaar-frisco-preston',
    name: 'India Bazaar Frisco',
    address: '8998 Preston Rd, Frisco, TX 75034',
    location: 'Frisco',
    placeQuery: 'India+Bazaar+8998+Preston+Rd+Frisco+TX+75034',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Bazaar+Frisco+8998+Preston+Rd+Frisco+TX+75034',
    image: IMG_INDIA_BAZAAR,
  },
  {
    id: 'india-bazaar-plano-angels',
    name: 'India Bazaar Plano',
    address: '8450 Angels Dr #100, Plano, TX 75024',
    location: 'Plano',
    placeQuery: 'India+Bazaar+8450+Angels+Dr+Plano+TX+75024',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=India+Bazaar+Plano+8450+Angels+Dr+Plano+TX+75024',
    image: IMG_INDIA_BAZAAR,
    instagram: 'https://www.instagram.com/p/DUoJolxiWJV/',
  },
  {
    id: 'patel-brothers-frisco-preston',
    name: 'Patel Brothers',
    address: '2787 Preston Rd #1150, Frisco, TX 75034',
    location: 'Frisco',
    placeQuery: 'Patel+Brothers+2787+Preston+Rd+Frisco+TX+75034',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Patel+Brothers+2787+Preston+Rd+Frisco+TX+75034',
    image: IMG_PATEL_BROTHERS,
  },
  {
    id: 'spice-rack-plano-mcdermott',
    name: 'Spice Rack',
    address: '2865 McDermott Rd #105, Plano, TX 75025',
    location: 'Plano',
    placeQuery: 'Spice+Rack+2865+McDermott+Rd+Plano+TX+75025',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Spice+Rack+2865+McDermott+Rd+Plano+TX+75025',
    image: IMG_SPICE_RACK,
  },
  {
    id: 'farm2cook-mckinney',
    name: 'FARM2COOK',
    address: '8720 State Hwy 121 Ste 126, McKinney, TX 75070',
    location: 'McKinney',
    placeQuery: 'FARM2COOK+8720+State+Hwy+121+McKinney+TX+75070',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=FARM2COOK+8720+State+Hwy+121+Ste+126+McKinney+TX+75070',
    image: IMG_FARM2COOK,
    type: 'Fresh Meat',
  },
  {
    id: 'farm2cook-frisco-eldorado',
    name: 'FARM2COOK',
    address: '14339 Eldorado Pkwy #180, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'FARM2COOK+14339+Eldorado+Pkwy+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=FARM2COOK+14339+Eldorado+Pkwy+180+Frisco+TX+75035',
    image: IMG_FARM2COOK,
    type: 'Fresh Meat',
  },
  {
    id: 'meat-wala-mckinney',
    name: 'Meat Wala',
    address: '6701 Collin McKinney Pkwy Suite 150, McKinney, TX 75070',
    location: 'McKinney',
    placeQuery: 'Meat+Wala+6701+Collin+McKinney+Pkwy+McKinney+TX+75070',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Meat+Wala+6701+Collin+McKinney+Pkwy+Suite+150+McKinney+TX+75070',
    image: IMG_MEAT_WALA,
    type: 'Fresh Meat',
  },
  {
    id: 'triveni-indian-super-market-irving',
    name: 'Triveni Indian Super Market',
    address: '839 N Belt Line Rd, Irving, TX 75061',
    location: 'Irving',
    placeQuery: 'Triveni+Indian+Super+Market+839+N+Belt+Line+Rd+Irving+TX+75061',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Triveni+Indian+Super+Market+839+N+Belt+Line+Rd+Irving+TX+75061',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784415948446-c8rijd.png',
  },
  {
    id: 'plano-halal-meats',
    name: 'Plano Halal Meats',
    address: '6100 K Ave Ste 102, Plano, TX 75074',
    location: 'Plano',
    placeQuery: 'Plano+Halal+Meats+6100+K+Ave+Ste+102+Plano+TX+75074',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Plano+Halal+Meats+6100+K+Ave+Ste+102+Plano+TX+75074',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784610291047-gf9t2.png',
    type: ['Fresh Meat', 'Halal'],
  },
  {
    id: 'foodistaan-groceries-aubrey',
    name: 'Foodistaan - Groceries, Halal Meat & Eatery',
    address: '27475 E University Dr, Aubrey, TX 76227',
    location: 'Aubrey',
    placeQuery: 'Foodistaan+27475+E+University+Dr+Aubrey+TX+76227',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Foodistaan+27475+E+University+Dr+Aubrey+TX+76227',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784678143837-9p02l.png',
    type: ['Fresh Meat', 'Halal'],
  },
];

export const FEATURED_GROCERY_IDS = new Set(['desi-mandi', 'meat-wala-mckinney', 'india-cash-carry', 'india-bazaar-plano-spring', 'india-bazaar-plano-angels']);

// ── Simple store card with hardcoded image ──
export function GroceryCard({ store, index, isFeatured }: { store: typeof GROCERY_STORES[0]; index: number; isFeatured?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={"/grocery/" + grocerySlug(store)} className="flex flex-col flex-1">
        {/* Store image */}
        <div className="w-full h-[80px] relative overflow-hidden bg-gray-100">
          <img
            src={store.image}
            alt={store.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className="p-2 pb-1 flex-1">
          <h3 className="text-[12px] font-bold text-gray-900 leading-tight mb-0.5 line-clamp-2">{store.name}</h3>
          <div className="flex items-start gap-1">
            <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
            <span className="text-[10px] text-gray-500 font-medium leading-snug line-clamp-2">{store.address}</span>
          </div>
          {/* Featured badge */}
          {isFeatured && (
            <div className="pt-1 flex items-center gap-1.5">
              <p className="text-[10px] font-semibold text-green-600 tracking-wide">
                Featured by TastebudsByAnubhi
              </p>
              <SocialLinks item={store.id === 'india-cash-carry' ? { ...store, tiktok: '', tiktokUrl: '', tiktok_url: '' } : store} px={20} />
            </div>
          )}
        </div>
      </Link>
      <div className="px-2 pb-2 pt-1">
        <a
          href={store.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 bg-[#f97316] hover:bg-[#ea580c] text-white text-[10px] font-semibold py-1 rounded-md transition-colors duration-200"
        >
          <ExternalLink className="w-2.5 h-2.5" />
          View Location
        </a>
      </div>
    </motion.div>
  );
}

export default function Grocery() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setDropdownSearch('');
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredHeroLocations = HERO_LOCATIONS.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  const mc = useManagedCollection<any>("grocery", GROCERY_STORES, (s, i) => slugify(s.name || String(i)));

  const effectiveLocation = selectedLocation ?? (selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null);
  const displayLocation = effectiveLocation ?? 'Dallas-DFW';

  // Some stores (e.g. India Bazaar at 832 Spring Creek Pkwy Suite 100, Plano;
  // India Metro Hypermarket at 1480 Independence Pkwy Ste 220, McKinney) ended
  // up listed more than once — either as duplicate default entries or
  // duplicate admin-added rows in the managed collection, sometimes with
  // slightly different address formatting ("Ste 220" vs "Suite #220", extra
  // spaces, punctuation). De-dupe by a loosely-normalized name+address key —
  // strip punctuation, collapse whitespace, and drop suite/ste/unit/#
  // numbers so near-identical formatting still matches — keeping the first
  // (lowest _order) copy so admin-managed ordering/edits on that card are
  // preserved.
  const ADDRESS_WORD_ALIASES: Record<string, string> = {
    street: 'st', st: 'st',
    road: 'rd', rd: 'rd',
    drive: 'dr', dr: 'dr',
    avenue: 'ave', av: 'ave', ave: 'ave',
    boulevard: 'blvd', blvd: 'blvd',
    parkway: 'pkwy', pkwy: 'pkwy',
    lane: 'ln', ln: 'ln',
    place: 'pl', pl: 'pl',
    east: 'e', e: 'e',
    west: 'w', w: 'w',
    north: 'n', n: 'n',
    south: 's', s: 's',
  };
  const normalizeAddress = (addr: string): string =>
    String(addr || '')
      .toLowerCase()
      // Strip explicit suite/ste/unit + number ("Suite 220", "Ste. #220")
      .replace(/\b(ste|suite|unit)\.?\s*#?\s*\d+[a-z]?\b/g, '')
      // Strip "#220" style unit markers
      .replace(/#\s*\d+[a-z]?\b/g, '')
      // Strip a bare leading unit number some listings prepend before the
      // street address instead of using a "Suite"/"#" keyword, e.g.
      // "4285, 2060 Spring Creek Pkwy" -> "2060 Spring Creek Pkwy"
      .replace(/^\s*\d+[a-z]?\s*,\s*/, '')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map((word) => ADDRESS_WORD_ALIASES[word] ?? word)
      .join(' ')
      .trim();
  const dedupedItems = (() => {
    const seen = new Set<string>();
    const out: typeof mc.items = [];
    for (const store of mc.items) {
      // Deleted cards should be gone for good — don't render them even as a
      // grayed-out "DELETED" admin placeholder; drop them before de-duping.
      if ((store as any)._removed) continue;
      const name = String((store as any).name || '').trim().toLowerCase();
      // Key on name + fully-normalized address (not just the location field):
      // the address string already contains the city, and normalizeAddress
      // strips suite/unit numbers, punctuation, and street-word abbreviations
      // (Pkwy/Parkway, Ste/Suite, etc). Keying on the raw location text alone
      // could still miss duplicates when location drifted between rows — the
      // India Metro Hypermarket McKinney card kept showing twice. Different
      // cities still produce different normalized addresses, so distinct
      // locations (McKinney vs Little Elm) stay distinct.
      const address = String((store as any).address || '').trim();
      // Swadeshi has shown up twice on this page with slightly different
      // admin-entered address formatting that doesn't normalize to an
      // identical string, so the general address-based key below misses the
      // duplicate. Each Swadeshi location is unique per city, so key it on
      // name+city instead — collapsing any duplicate row to one card.
      const key = name === 'swadeshi'
        ? `${name}|${String((store as any).location || '').trim().toLowerCase()}`
        : `${name}|${normalizeAddress(address)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(store);
    }
    return out;
  })();

  const filteredStores = dedupedItems.filter((store) => {
    const matchesQuery = searchQuery.trim() === '' || store.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !effectiveLocation || store.location === effectiveLocation;
    const storeTypes: string[] = Array.isArray((store as any).type) ? (store as any).type : [(store as any).type].filter(Boolean);
    const isMeatStore = isFreshMeatStore(store as any) || storeTypes.includes('Fresh Meat');
    const isIndiaBazaar = String(store.name || '').toLowerCase().includes('bazaar') || String(store.name || '').toLowerCase().includes('bazar');
    // Explicit exclusion by user request: India Metro Hypermarket (McKinney,
    // 1480 Independence Pkwy Ste 220) never shows under Fresh Meat or Halal,
    // regardless of its `type` field or any future admin override. Matched by
    // BOTH the stable default id AND a normalized name+address check — an
    // admin-added duplicate row (via the Admin panel) has no
    // `id: 'india-metro-mckinney'` field at all, so the id-only check missed
    // it and the card kept reappearing under these filters even after being
    // excluded once. The name+address match catches that duplicate too.
    const normalizedStoreName = String(store.name || '').trim().toLowerCase();
    const normalizedStoreAddress = normalizeAddress(String((store as any).address || ''));
    const isIndiaMetroMckinneyDuplicate =
      normalizedStoreName === 'india metro hypermarket' &&
      normalizedStoreAddress.includes('1480 independence pkwy') &&
      normalizedStoreAddress.includes('mckinney');
    const isExcludedFromMeatHalal = store.id === 'india-metro-mckinney' || isIndiaMetroMckinneyDuplicate;
    const eligibleForMeat = isMeatStore && !isIndiaBazaar && !isExcludedFromMeatHalal;
    // Every Fresh Meat store also shows under Halal (per user request), in
    // addition to any store explicitly tagged Halal via its `type` field.
    const isHalalStore = !isExcludedFromMeatHalal && (storeTypes.includes('Halal') || eligibleForMeat);
    const matchesType = !selectedType
      ? true
      : selectedType === 'Fresh Meat'
        ? eligibleForMeat
        : selectedType === 'Halal'
          ? isHalalStore
          : storeTypes.includes(selectedType);
    return matchesQuery && matchesLocation && matchesType;
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical, so
  // editing a card doesn't move it and reordering persists on the public site.
  const sortedStores = [...filteredStores].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));
  const featuredStores = sortedStores.filter((s) => FEATURED_GROCERY_IDS.has(s.id));
  // "More Grocery" pinned/sort order (explicit user request): the two Spice
  // Rack cards always lead, followed by the rest alphabetically by name.
  // NOTE: this intentionally overrides admin drag-reorder (_order) for this
  // section — the user asked for a fixed pinned+alphabetical order here, so
  // dragging cards within "More Grocery" will no longer change their
  // position.
  const SPICE_RACK_IDS = ['spice-rack-grocery-frisco', 'spice-rack-grocery', 'spice-rack-plano-mcdermott'];
  const nonFeatured = sortedStores.filter((s) => !FEATURED_GROCERY_IDS.has(s.id));
  const spiceRackStores = SPICE_RACK_IDS
    .map((id) => nonFeatured.find((s: any) => s.id === id))
    .filter(Boolean) as typeof nonFeatured;
  const restStores = nonFeatured
    .filter((s: any) => !SPICE_RACK_IDS.includes(s.id))
    .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
  const nonFeaturedStores = [...spiceRackStores, ...restStores];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1440&h=400&fit=crop"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find a store</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          ref={dropdownInputRef}
                          type="text"
                          value={dropdownSearch}
                          onChange={(e) => setDropdownSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                        />
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {filteredHeroLocations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No locations found</div>
                        ) : (
                          filteredHeroLocations.map((loc) => (
                            <button
                              key={loc}
                              onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); setDropdownSearch(''); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                fontWeight: selectedHeroLocation === loc ? 700 : 500,
                                color: selectedHeroLocation === loc ? '#16a34a' : '#111827',
                                background: selectedHeroLocation === loc ? '#f0fdf4' : 'transparent',
                                height: '36px',
                              }}
                              onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#f0fdf4'; }}
                              onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              {loc}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#16a34a] hover:bg-[#15803d] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav removed */}


      {/* ── Grocery Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>
              Top Indian, Pakistani &amp; South Asian Grocery Stores in Dallas–Fort Worth
            </h1>

            {(() => {
              const introParagraphs = [
                "Discover the top Indian, Pakistani, and South Asian grocery stores across the Dallas–Fort Worth area with DallasDesiScoop. Find fresh produce, Indian and Pakistani spices, rice, lentils, snacks, sweets, frozen foods, dairy, fresh meat, halal meat, and everyday groceries all in one place. Explore grocery stores in Dallas, Frisco, Plano, Irving, McKinney, Allen, The Colony, Prosper, Lewisville, Carrollton, Coppell, Richardson, Flower Mound, Little Elm, and surrounding DFW communities. Find Indian grocery stores, Pakistani grocery stores, South Asian supermarkets, halal meat stores, fresh meat markets, and specialty food stores serving the growing Desi community. Whether you're looking for Indian groceries near you, Pakistani groceries, South Asian ingredients, fresh produce, halal meat, fresh meat, spices, snacks, or everyday essentials, DallasDesiScoop helps you discover grocery stores across DFW.",
              ];
              const [firstParagraph, ...restParagraphs] = introParagraphs;

              return (
                <>
                  {/* Mobile: first paragraph + Read more toggle */}
                  <div className="sm:hidden">
                    <p className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Lora', serif" }}>
                      {firstParagraph}
                    </p>
                    {restParagraphs.length > 0 && (
                      <>
                        {isDescExpanded && restParagraphs.map((para, i) => (
                          <p key={i} className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Lora', serif" }}>
                            {para}
                          </p>
                        ))}
                        <button
                          type="button"
                          onClick={() => setIsDescExpanded((v) => !v)}
                          className="text-[12px] font-semibold mb-4"
                          style={{ fontFamily: "'Lora', serif", color: '#16a34a' }}
                        >
                          {isDescExpanded ? 'Read less' : 'Read more'}
                        </button>
                      </>
                    )}
                    {restParagraphs.length === 0 && <div className="mb-4" />}
                  </div>

                  {/* Desktop / tablet: full text, no truncation */}
                  <div className="hidden sm:block">
                    {introParagraphs.map((para, i) => (
                      <p
                        key={i}
                        className={`text-[13px] text-gray-500 ${i === introParagraphs.length - 1 ? 'mb-6' : 'mb-2'}`}
                        style={{ fontFamily: "'Lora', serif" }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </>
              );
            })()}
            {/* Explore by Type */}
            <p className="text-[11px] sm:text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Explore by Type</p>
            {/* Type filter chips */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto filter-scroll pb-1">
              {GROCERY_TYPES.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => setSelectedType(selectedType === t.name ? null : t.name)}
                  className={"shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium border transition-all duration-200 " +
                    (selectedType === t.name ? "bg-green-50 text-green-700 border-green-300" : "bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50/50")}
                >
                  <span>{t.icon}</span>
                  {t.name}
                </button>
              ))}
              {selectedType && (
                <button
                  type="button"
                  onClick={() => setSelectedType(null)}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium border border-gray-300 bg-white text-gray-600 hover:border-red-300 hover:text-red-600 hover:bg-red-50/50 transition-all duration-200"
                >
                  <X className="w-3 h-3" />
                  Clear filters
                </button>
              )}
            </div>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Find spices, fresh produce, and specialty ingredients across Frisco, Plano, Irving, Lewisville, and McKinney.
            </p>
            <AdminAddBar mc={mc} fields={GROCERY_FIELDS} label="Add Store" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredStores.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {featuredStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={GROCERY_FIELDS} title="Store">
                      <GroceryCard store={store} index={index} isFeatured />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {/* More Grocery */}
            {nonFeaturedStores.length > 0 && (
              <div>
                {featuredStores.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    More Grocery
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {nonFeaturedStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={GROCERY_FIELDS} title="Store">
                      <GroceryCard store={store} index={index} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {filteredStores.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No grocery stores found for this search.</p>
              </div>
            )}

            {/* SEO footer copy */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <p className="text-[13px] text-gray-500 leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>
                Looking for Indian groceries near you, Pakistani groceries near you, or a South Asian supermarket in DFW? DallasDesiScoop helps you discover local grocery stores for Indian and Pakistani spices, basmati rice, lentils and dals, Indian snacks, sweets, frozen foods, fresh vegetables, dairy products, halal meat, fresh meat markets, and specialty ingredients. Whether you need groceries for everyday cooking, ingredients for Indian or Pakistani recipes, halal food, or hard-to-find South Asian products, explore trusted Desi grocery stores and food markets throughout the Dallas–Fort Worth area.
              </p>
              <p className="mt-4" style={{ fontFamily: "'Lora', serif" }}>
                <Link
                  to="/blog/best-indian-grocery-stores-dfw"
                  className="text-[13px] font-semibold underline underline-offset-2"
                  style={{ color: '#16a34a' }}
                >
                  Read our DFW grocery guide →
                </Link>
              </p>
              <p className="mt-2" style={{ fontFamily: "'Lora', serif" }}>
                <Link
                  to="/blog"
                  className="text-[13px] font-semibold underline underline-offset-2"
                  style={{ color: '#16a34a' }}
                >
                  Explore all DFW Desi guides on our blog →
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
